import { useState, type ReactNode } from "react";

import { useLanguage } from "../../i18n/language-provider";
import { includedInBoth } from "../legal/priceListCopy";
import { SUPPORT_RESPONSE_HOURS } from "../support/support-contact";
import {
    CompetitorSketch,
    HolidaySketch,
    MinStaySketch,
    MonitorSketch,
    PriceHistorySketch,
    RatePlanSketch,
    SyncSketch,
    WeatherSketch,
} from "./FeatureSketches";

interface FeatureBentoProps {
    /** The moment the sketches count from; now unless a test pins it. */
    now?: Date;
}

/**
 * What Optivo does, in two tiers. The capabilities that price a night lead as
 * an asymmetric grid of unequal tiles, each carrying a small sketch of the
 * product doing what the tile says — a night's price history, the rail's sync
 * chip, the pace monitor, the market's median — drawn with the same tokens
 * and words the product uses, rather than an icon standing in for the claim.
 * The rest follows as a ruled list: true of the product, and nothing a sketch
 * would add to.
 *
 * Every claim here is one the product keeps (`FeatureBento.test.tsx` →
 * "should promise only what the product does").
 */
export function FeatureBento({ now }: FeatureBentoProps) {
    const { t } = useLanguage();
    const w = t.welcome;
    const [origin] = useState(() => now ?? new Date());

    const more = [
        {
            title: w.featureHorizonTitle,
            // The horizon the price list promises, which is the one the engine prices.
            description: w.featureHorizonDesc.replace("{nights}", String(includedInBoth().horizonDays)),
        },
        { title: w.featureHistoryTitle, description: w.featureHistoryDesc },
        { title: w.featureControlTitle, description: w.featureControlDesc },
        {
            title: w.featureSupportTitle,
            // The reply promise the app itself makes wherever it invites a question.
            description: w.featureSupportDesc.replace("{hours}", String(SUPPORT_RESPONSE_HOURS)),
        },
        { title: w.featureSecurityTitle, description: w.featureSecurityDesc },
        { title: w.featureSmallTitle, description: w.featureSmallDesc },
    ];

    return (
        <>
            {/* One explicit column on a phone: an implicit one grows to its
                widest tile's longest unbreakable line and takes every tile,
                and the page, past the screen with it. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-6">
                <FeatureTile
                    className="sm:col-span-2 md:col-span-4 md:row-span-2"
                    title={w.featureDynamicTitle}
                    description={w.featureDynamicDesc}
                >
                    <PriceHistorySketch />
                </FeatureTile>
                <FeatureTile className="md:col-span-2" title={w.featurePmsTitle} description={w.featurePmsDesc}>
                    <SyncSketch now={origin} />
                </FeatureTile>
                <FeatureTile className="md:col-span-2" title={w.featureMonitorTitle} description={w.featureMonitorDesc}>
                    <MonitorSketch now={origin} />
                </FeatureTile>
                <FeatureTile
                    className="md:col-span-2"
                    title={w.featureCompetitorsTitle}
                    description={w.featureCompetitorsDesc}
                >
                    <CompetitorSketch now={origin} />
                </FeatureTile>
                <FeatureTile className="md:col-span-2" title={w.featureWeatherTitle} description={w.featureWeatherDesc}>
                    <WeatherSketch now={origin} />
                </FeatureTile>
                <FeatureTile
                    className="sm:col-span-2 md:col-span-2"
                    title={w.featureHolidaysTitle}
                    description={w.featureHolidaysDesc}
                >
                    <HolidaySketch now={origin} />
                </FeatureTile>
                <FeatureTile
                    className="md:col-span-3"
                    title={w.featureRatePlansTitle}
                    description={w.featureRatePlansDesc}
                >
                    <RatePlanSketch now={origin} />
                </FeatureTile>
                <FeatureTile className="md:col-span-3" title={w.featureMinStayTitle} description={w.featureMinStayDesc}>
                    <MinStaySketch now={origin} />
                </FeatureTile>
            </div>

            <div className="mt-14">
                <p className="type-eyebrow">{w.featureMoreLabel}</p>
                <ul className="mt-5 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                    {more.map((item) => (
                        <li key={item.title} className="border-t border-foreground/80 pt-4">
                            <h3 className="type-display-section text-lg">{item.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
}

interface FeatureTileProps {
    title: string;
    description: string;
    className?: string;
    children?: ReactNode;
}

function FeatureTile({ title, description, className = "", children }: FeatureTileProps) {
    return (
        <article className={`flex flex-col rounded-lg border bg-card p-6 text-card-foreground ${className}`}>
            <span aria-hidden="true" className="block h-0.5 w-6 bg-emphasis" />
            <h3 className="type-display-section mt-4 text-xl text-balance">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
            {/* A sketch sits on the tile's foot, so tiles side by side line
                theirs up; one that grows (the price history) fills it. */}
            {children && <div className="mt-6 flex flex-1 flex-col justify-end">{children}</div>}
        </article>
    );
}
